// useStudioCommands — the Studio's module commands (pen B4), in the shell,
// shared by the toolbar, the card menus, a drop of one card on another and a
// container's part rows (move, remove; each with an Undo):
// each runs one port command, reloads the library so the canvas projects the
// result, and reports a refusal as a notice (the domain's reason). `onCreated` hands the new
// resource's id back so the canvas can place its card where it was made.
// The canvas is the project the author is in (`project`; Studio v1 D1):
// placing, moving and taking a card off are that project's commands, so the
// layout is shared project state; taking a card off keeps where it lay, for
// the Undo. It also keeps the card to reveal when an upload was already
// there (view state). A rename (MF-1) shows at once everywhere the library
// is read (desk, breadcrumb, Created materials, tabs), and goes back if the
// save fails.

export type { NewStudioMaterial, StudioCommands };
export { useStudioCommands };

import { Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import { type Dispatch, type SetStateAction, useState } from "react";
import type { ContainerPart } from "../../components/Studio/studioCards.js";
import {
    type DeskCommand,
    type DeskState,
    deskReduce,
} from "../../components/Studio/studioDesk.js";
import type { Point } from "../../components/Studio/studioLayout.js";
import {
    type DeskNote,
    deskNotes,
    noteOfRefusal,
} from "../../components/StudioDesk/deskFacts.js";
import type { ContainerKind } from "../containerFacts.js";
import { numberedDefaultName } from "../createDefaults.js";
import type { ExternalError } from "../externalError.js";
import type { HostNotice } from "../frame.js";
import type { PortResult } from "../ports/result.js";
import type { AppPorts, Library } from "../ports.js";
import { writeProjectPlace } from "../projectsPreference.js";
import { writeStudioDesk } from "../studioLayoutPreference.js";
import { duplicateModule, moduleCount } from "./duplicateModule.js";
import { useDeskJobs } from "./useDeskJobs.js";
import { type StudioCreate, useStudioCreate } from "./useStudioCreate.js";
import type { StudioPlace } from "./useStudioPlace.js";

// The empty materials the toolbar's Material menu makes: one per template
// the domain has (no lesson template yet).
type NewStudioMaterial = "quiz" | "document";

type StudioCommands = {
    // The cards (reading order) become the parts of a new module or course.
    readonly putInNewModule: (
        parts: readonly string[],
        onCreated: (moduleId: string) => void,
    ) => void;
    readonly putInNewCourse: (
        parts: readonly string[],
        onCreated: (courseId: string) => void,
    ) => void;
    // The cards go at the end of a module or course.
    readonly addToModule: (moduleId: string, parts: readonly string[]) => void;
    // The Undo of an add: the parts come back out (they stay on the canvas).
    readonly removeFromModule: (
        moduleId: string,
        parts: readonly string[],
    ) => void;
    // A part to another place in its container (`from` is where it is now).
    readonly movePart: (
        moduleId: string,
        part: string,
        from: number,
        to: number,
    ) => void;
    // The Undo of a move: back to `index`, quietly.
    readonly movePartBack: (
        moduleId: string,
        part: string,
        index: number,
    ) => void;
    // A part out of its container (it stays on the canvas); `at` is its place.
    readonly removePart: (
        moduleId: string,
        kind: ContainerKind,
        part: string,
        at: number,
    ) => void;
    // A part from one container into another, at its end: in there first,
    // then out of here (a refusal half-way leaves it in both, never in none).
    readonly movePartTo: (from: string, part: string, to: string) => void;
    // The Undo of a remove: in again, at its old place, quietly.
    readonly putPartBack: (moduleId: string, part: string, at: number) => void;
    readonly newMaterial: (
        kind: NewStudioMaterial,
        onCreated: (materialId: string) => void,
    ) => void;
    // A card onto the project's canvas at `at` (a new one, or picked from
    // the canvas menu; no `at`: beside what is there, e.g. an upload), and a
    // card on it to a new place. A card already there stays where it is.
    readonly placeOnCanvas: (id: string, at?: Point) => void;
    readonly moveOnCanvas: (id: string, at: Point) => void;
    // Off the canvas (never deleted), with an Undo; `title` names the first.
    readonly removeFromCanvas: (ids: readonly string[], title: string) => void;
    // The Undo: back where they lay.
    readonly putBackOnCanvas: (ids: readonly string[]) => void;
    // A new name, shown at once; a failed save puts `previous` back, then
    // `onFailed`.
    readonly rename: (
        id: string,
        title: string,
        previous: string,
        onFailed: () => void,
    ) => void;
    // A card to highlight and bring into view (an upload that was already
    // there); `nonce` tells a repeat apart. View state.
    readonly revealed: StudioReveal | null;
    readonly revealOnCanvas: (id: string) => void;
    // The page the Studio shows (view state, per device; studioDesk.ts):
    // changes to it, in order.
    readonly desk: DeskState;
    readonly onDesk: (...commands: readonly DeskCommand[]) => void;
    // Back to a page left earlier (opening a project where the author was).
    readonly showPlace: (desk: DeskState) => void;
    // Where the author is for the assistant, the caret the editor reports,
    // and the composer's scope chip (useStudioPlace.ts).
    readonly location: StudioPlace["location"];
    readonly noteCaret: StudioPlace["noteCaret"];
    readonly scopeToWhole: StudioPlace["scopeToWhole"];
    readonly scopeToPage: StudioPlace["scopeToPage"];
    // The project's purpose (its brief, #282): say it, or clear it.
    readonly describeProject: (purpose: string) => void;
    readonly clearProjectDescription: () => void;
    // The project's learning objectives: a new one in the author's words
    // (last in the list); one taken off (the objective itself stays).
    readonly addObjective: (statement: string) => void;
    readonly removeObjective: (objectiveId: string) => void;
    // A module's next one in its course, right after it: the same parts,
    // planned and empty (PS-43).
    readonly duplicateModule: (courseId: string, module: ContainerPart) => void;
    // Generate a container's planned parts from the project's sources (the
    // Needs you tab's "Generate it"; parts are planned through `create`).
    // What came of it shows at the document (`notes`, by id: a refusal, or
    // a started job reading the sources first, useDeskJobs.ts; view state),
    // never as a message.
    readonly generateAll: (containerId: string, ids: readonly string[]) => void;
    readonly notes: ReadonlyMap<string, DeskNote>;
    // Make a part: the Create menu and "/" at the caret (useStudioCreate).
    readonly create: StudioCreate;
};

type StudioReveal = { readonly id: string; readonly nonce: number };

type StudioCommandsDeps = {
    readonly ports: AppPorts;
    // The loaded library (what a module already holds).
    readonly library: Library | null;
    readonly setLibrary: Dispatch<SetStateAction<Library | null>>;
    readonly showNotice: (notice: HostNotice) => void;
    readonly displayText: (error: ExternalError) => string;
    // A title changed: the open tabs and recents that carry it follow.
    readonly retitle: (id: string, title: string) => void;
    // Where the author is in the Studio (held by the shell: the Editor's
    // front document is the edited material while the Studio is shown).
    readonly place: StudioPlace;
    // The project the author is in (its canvas); null: none chosen.
    readonly project: string | null;
};

const useStudioCommands = ({
    ports,
    library,
    setLibrary,
    showNotice,
    displayText,
    retitle,
    place,
    project,
}: StudioCommandsDeps): StudioCommands => {
    const { t } = useLingui();
    // Where the cards last taken off lay (view state, for their Undo).
    const [takenOff, setTakenOff] = useState<ReadonlyMap<string, Point>>(
        new Map(),
    );
    const [revealed, setRevealed] = useState<StudioReveal | null>(null);
    const [notes, setNotes] = useState<ReadonlyMap<string, DeskNote>>(
        new Map(),
    );
    const jobs = useDeskJobs(ports);
    const create = useStudioCreate({
        ports,
        library,
        setLibrary,
        showNotice,
        displayText,
        place,
        project,
    });
    const note = (id: string, next: DeskNote | null) =>
        setNotes((m) => {
            const out = new Map(m);
            if (next === null) out.delete(id);
            else out.set(id, next);
            return out;
        });

    // Run `command`; on success reload the library, then `after` (with the
    // reloaded library, or null if that failed).
    const run = <A>(
        command: () => PortResult<A>,
        after: (value: A, library: Library | null) => void,
    ): void => {
        const go = async () => {
            const r = await command();
            if (Result.isErr(r)) {
                showNotice({
                    type: "lifecycle-failed",
                    message: displayText(r.error),
                });
                return;
            }
            const lib = await ports.loadLibrary();
            if (Result.isOk(lib)) setLibrary(lib.value);
            after(r.value, Result.isOk(lib) ? lib.value : null);
        };
        void go();
    };

    // A command on the project the author is in (none chosen: nothing).
    // Commands on the project the author is in, one after another (each
    // changes the same project; the first refusal stops the rest). None
    // chosen: nothing.
    const onProject = (
        ...commands: readonly ((projectId: string) => PortResult<void>)[]
    ) => {
        if (project === null) return;
        run(
            () =>
                commands.reduce<PortResult<void>>(
                    (done, command) =>
                        done.then((r) =>
                            Result.isErr(r) ? r : command(project),
                        ),
                    Promise.resolve(Result.ok(undefined)),
                ),
            () => undefined,
        );
    };

    // Run `command` for `id`'s note: "starting" while it runs (`starting`);
    // a refusal becomes the note; done, `done` gets the answer and the
    // library shows the result.
    const attempt = <A>(
        id: string,
        command: () => PortResult<A>,
        starting: boolean,
        done: (value: A) => void = () => undefined,
    ): void => {
        const go = async () => {
            if (starting) note(id, { type: "starting" });
            const r = await command();
            if (Result.isErr(r)) {
                note(
                    id,
                    noteOfRefusal(
                        r.error._tag === "External.Refused"
                            ? r.error.errorType
                            : undefined,
                        displayText(r.error),
                    ),
                );
                return;
            }
            done(r.value);
            note(id, null);
            const lib = await ports.loadLibrary();
            if (Result.isOk(lib)) setLibrary(lib.value);
        };
        void go();
    };

    // The page shown, kept on this device, and remembered as where the
    // author is in this project (All projects: "You were in <page>").
    const showPlace = (next: DeskState) => {
        place.setDesk(next);
        writeStudioDesk(next);
        if (project !== null) writeProjectPlace(project, next);
    };
    const setPlace = (commands: readonly DeskCommand[]) =>
        showPlace(commands.reduce(deskReduce, place.desk));

    // The title everywhere the library and the tabs show it.
    const showTitle = (id: string, title: string) => {
        setLibrary((l) => (l === null ? l : retitled(l, id, title)));
        retitle(id, title);
    };

    const moduleTitle = t`New module`;
    const courseTitle = t`New course`;
    return {
        rename: (id, title, previous, onFailed) => {
            showTitle(id, title);
            const go = async () => {
                const r = await ports.renameResource(id, title);
                if (Result.isErr(r)) {
                    showTitle(id, previous);
                    onFailed();
                    return;
                }
                const lib = await ports.loadLibrary();
                if (Result.isOk(lib)) setLibrary(lib.value);
            };
            void go();
        },
        putInNewCourse: (parts, onCreated) =>
            run(
                () => ports.putInNewCourse(courseTitle, parts),
                ({ materialId }) => {
                    onCreated(materialId);
                    showNotice({
                        type: "course-made",
                        ref: {
                            type: "material",
                            id: materialId,
                            title: courseTitle,
                        },
                        count: parts.length,
                    });
                },
            ),
        putInNewModule: (parts, onCreated) =>
            run(
                () => ports.putInNewModule(moduleTitle, parts),
                ({ materialId }) => {
                    onCreated(materialId);
                    showNotice({
                        type: "module-made",
                        ref: {
                            type: "material",
                            id: materialId,
                            title: moduleTitle,
                        },
                        count: parts.length,
                    });
                },
            ),
        addToModule: (moduleId, parts) => {
            // A card already in it: nothing to do; the part is shown at the
            // object instead of a refusal toast (pen "Messages" §1).
            const [part] = parts;
            const held =
                library?.materials.find((m) => m.id === moduleId)?.parts ?? [];
            const at =
                parts.length === 1 && part !== undefined
                    ? held.indexOf(part)
                    : -1;
            if (part !== undefined && at >= 0) {
                showNotice({
                    type: "already-part",
                    moduleId,
                    part,
                    number: at + 1,
                });
                return;
            }
            run(
                () => ports.addToModule(moduleId, parts),
                (change, library) =>
                    showNotice({
                        type: "parts-added",
                        moduleId: change.moduleId,
                        parts: change.parts,
                        // The first added part's number in the module.
                        number:
                            (library?.materials
                                .find((m) => m.id === change.moduleId)
                                ?.parts?.indexOf(change.parts[0] ?? "") ?? -1) +
                            1,
                    }),
            );
        },
        removeFromModule: (moduleId, parts) =>
            run(
                () => ports.removeFromModule(moduleId, parts),
                () => undefined,
            ),
        movePart: (moduleId, part, from, to) =>
            run(
                () => ports.reorderPart(moduleId, part, to),
                (moved) => {
                    if (moved.from !== moved.to)
                        showNotice({
                            type: "part-moved",
                            moduleId,
                            part,
                            from,
                            to: moved.to,
                        });
                },
            ),
        movePartBack: (moduleId, part, index) =>
            run(
                () => ports.reorderPart(moduleId, part, index),
                () => undefined,
            ),
        removePart: (moduleId, kind, part, at) =>
            run(
                () => ports.removeFromModule(moduleId, [part]),
                () =>
                    showNotice({
                        type: "part-removed",
                        moduleId,
                        part,
                        at,
                        kind,
                    }),
            ),
        movePartTo: (from, part, to) =>
            run(
                async () => {
                    const added = await ports.addToModule(to, [part]);
                    return Result.isErr(added)
                        ? added
                        : ports.removeFromModule(from, [part]);
                },
                () => undefined,
            ),
        // Two commands: in at the end, then to its place.
        putPartBack: (moduleId, part, at) =>
            run(
                async () => {
                    const added = await ports.addToModule(moduleId, [part]);
                    return Result.isErr(added)
                        ? added
                        : ports.reorderPart(moduleId, part, at);
                },
                () => undefined,
            ),
        newMaterial: (kind, onCreated) =>
            run(
                () =>
                    ports.createMaterial(
                        kind === "quiz"
                            ? {
                                  templateId: "T_quiz",
                                  title: numberedDefaultName(
                                      materialTitles(library),
                                      t`New quiz`,
                                  ),
                              }
                            : {
                                  templateId: "T_freeform",
                                  title: numberedDefaultName(
                                      materialTitles(library),
                                      t`New document`,
                                  ),
                              },
                    ),
                ({ materialId }) => onCreated(materialId),
            ),
        generateAll: (containerId, ids) =>
            attempt(
                containerId,
                () => ports.startGeneration(containerId, ids),
                true,
                jobs.follow,
            ),
        notes: deskNotes(notes, jobs.jobs.values()),
        create,
        placeOnCanvas: (id, at) => {
            const lay = canvasOf(library, project);
            if (lay.has(id)) return;
            onProject((p) =>
                ports.placeOnProjectCanvas(p, id, at ?? besideAll(lay)),
            );
        },
        moveOnCanvas: (id, at) =>
            onProject((p) => ports.moveOnProjectCanvas(p, id, at)),
        removeFromCanvas: (ids, title) => {
            const lay = canvasOf(library, project);
            setTakenOff(
                new Map(
                    ids.flatMap((id): [string, Point][] => {
                        const at = lay.get(id);
                        return at === undefined ? [] : [[id, at]];
                    }),
                ),
            );
            onProject(
                ...ids.map(
                    (id) => (p: string) => ports.takeOffProjectCanvas(p, id),
                ),
            );
            showNotice({ type: "removed-from-canvas", ids, title });
        },
        putBackOnCanvas: (ids) => {
            onProject(
                ...ids.flatMap((id) => {
                    const at = takenOff.get(id);
                    return at === undefined
                        ? []
                        : [
                              (p: string) =>
                                  ports.placeOnProjectCanvas(p, id, at),
                          ];
                }),
            );
        },
        revealed,
        revealOnCanvas: (id) =>
            setRevealed({ id, nonce: (revealed?.nonce ?? 0) + 1 }),
        desk: place.desk,
        onDesk: (...commands) => setPlace(commands),
        showPlace,
        location: place.location,
        noteCaret: place.noteCaret,
        scopeToWhole: place.scopeToWhole,
        scopeToPage: place.scopeToPage,
        describeProject: (purpose) =>
            onProject((p) => ports.setProjectDescription(p, purpose)),
        clearProjectDescription: () =>
            onProject((p) => ports.clearProjectDescription(p)),
        addObjective: (statement) =>
            onProject((p) => ports.addObjectiveToProject(p, statement)),
        removeObjective: (objectiveId) =>
            onProject((p) => ports.removeObjectiveFromProject(p, objectiveId)),
        duplicateModule: (courseId, module) =>
            run(
                () =>
                    duplicateModule(
                        ports,
                        courseId,
                        module,
                        t`Module ${moduleCount(library, courseId) + 1}`,
                        (
                            library?.materials.find((m) => m.id === courseId)
                                ?.parts ?? []
                        ).indexOf(module.id),
                    ),
                () => undefined,
            ),
    };
};

// Where each card lies on the project's canvas (empty: no such project).
const canvasOf = (
    library: Library | null,
    project: string | null,
): ReadonlyMap<string, Point> =>
    new Map(
        (library?.projects ?? [])
            .find((p) => p.id === project)
            ?.canvas.map((item): [string, Point] => [
                item.target,
                { x: item.x, y: item.y },
            ]) ?? [],
    );

// A place to the right of every card on the canvas, level with the topmost
// (an empty canvas: its origin). Canvas units; a card is 260 wide.
const BESIDE_GAP = 320;
const besideAll = (canvas: ReadonlyMap<string, Point>): Point => {
    const at = [...canvas.values()];
    return at.length === 0
        ? { x: 0, y: 0 }
        : {
              x: Math.max(...at.map((p) => p.x)) + BESIDE_GAP,
              y: Math.min(...at.map((p) => p.y)),
          };
};

// A resource's title, in whichever list holds it.
const retitled = (library: Library, id: string, title: string): Library => {
    const each = <T extends { readonly id: string }>(
        items: readonly T[],
    ): readonly T[] =>
        items.map((item) => (item.id === id ? { ...item, title } : item));
    return {
        ...library,
        sources: each(library.sources),
        materials: each(library.materials),
        exports: each(library.exports),
        files: each(library.files),
    };
};

// Every material's name, archived ones included: a new one is numbered
// against them (#211), so "New quiz 2" never repeats and never shifts.
const materialTitles = (library: Library | null): readonly string[] =>
    [...(library?.materials ?? []), ...(library?.archived ?? [])].flatMap(
        (m) =>
            m.type === "material" && m.title !== undefined ? [m.title] : [],
    );
